import { collection, addDoc, query, where, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface Message {
  id?: string;
  fromUserId: string;
  fromUserEmail: string;
  fromUserName: string;
  toUserId?: string;
  toUserEmail?: string;
  toUserName?: string;
  subject: string;
  message: string;
  type: 'quote_inquiry' | 'quote_response' | 'invoice_notification' | 'payment_notification' | 'general';
  relatedQuoteId?: string;
  relatedInvoiceId?: string;
  relatedBillId?: string;
  isRead: boolean;
  isAdminMessage: boolean;
  timestamp: string;
  createdAt?: any;
}

export interface Notification {
  id?: string;
  userId: string;
  userEmail: string;
  title: string;
  message: string;
  type: 'quote_received' | 'quote_approved' | 'invoice_generated' | 'payment_received' | 'message_received';
  isRead: boolean;
  relatedId?: string;
  timestamp: string;
  createdAt?: any;
}

class CommunicationService {
  // Send a message between users
  async sendMessage(messageData: Omit<Message, 'id' | 'timestamp' | 'createdAt'>): Promise<string> {
    try {
      const message: Omit<Message, 'id'> = {
        ...messageData,
        timestamp: new Date().toISOString(),
        createdAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'messages'), message);
      console.log('Message sent successfully:', docRef.id);

      // Create notification for recipient
      if (messageData.toUserEmail) {
        await this.createNotification({
          userId: messageData.toUserId || '',
          userEmail: messageData.toUserEmail,
          title: `New Message: ${messageData.subject}`,
          message: messageData.message.substring(0, 100) + (messageData.message.length > 100 ? '...' : ''),
          type: 'message_received',
          isRead: false,
          relatedId: docRef.id
        });
      }

      return docRef.id;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  // Get messages for a user (inbox)
  async getMessagesForUser(userEmail: string, limit: number = 50): Promise<Message[]> {
    try {
      const q = query(
        collection(db, 'messages'),
        where('toUserEmail', '==', userEmail)
      );

      const querySnapshot = await getDocs(q);
      const messages: Message[] = [];

      querySnapshot.forEach((doc) => {
        messages.push({
          id: doc.id,
          ...doc.data()
        } as Message);
      });

      // Sort by timestamp in JavaScript instead of Firestore
      messages.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      return messages.slice(0, limit);
    } catch (error) {
      console.error('Error fetching messages:', error);
      return [];
    }
  }

  // Get sent messages for a user
  async getSentMessagesForUser(userEmail: string, limit: number = 50): Promise<Message[]> {
    try {
      const q = query(
        collection(db, 'messages'),
        where('fromUserEmail', '==', userEmail)
      );

      const querySnapshot = await getDocs(q);
      const messages: Message[] = [];

      querySnapshot.forEach((doc) => {
        messages.push({
          id: doc.id,
          ...doc.data()
        } as Message);
      });

      // Sort by timestamp in JavaScript instead of Firestore
      messages.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      return messages.slice(0, limit);
    } catch (error) {
      console.error('Error fetching sent messages:', error);
      return [];
    }
  }

  // Get all messages for admin
  async getAllMessagesForAdmin(limit: number = 100): Promise<Message[]> {
    try {
      const q = query(
        collection(db, 'messages')
      );

      const querySnapshot = await getDocs(q);
      const messages: Message[] = [];

      querySnapshot.forEach((doc) => {
        messages.push({
          id: doc.id,
          ...doc.data()
        } as Message);
      });

      // Sort by timestamp in JavaScript instead of Firestore
      messages.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      return messages.slice(0, limit);
    } catch (error) {
      console.error('Error fetching all messages:', error);
      return [];
    }
  }

  // Mark message as read
  async markMessageAsRead(messageId: string): Promise<void> {
    try {
      await updateDoc(doc(db, 'messages', messageId), {
        isRead: true
      });
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  }

  // Create a notification
  async createNotification(notificationData: Omit<Notification, 'id' | 'timestamp' | 'createdAt'>): Promise<string> {
    try {
      const notification: Omit<Notification, 'id'> = {
        ...notificationData,
        timestamp: new Date().toISOString(),
        createdAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'notifications'), notification);
      console.log('Notification created successfully:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('Error creating notification:', error);
      throw error;
    }
  }

  // Get notifications for a user
  async getNotificationsForUser(userEmail: string, limit: number = 20): Promise<Notification[]> {
    try {
      const q = query(
        collection(db, 'notifications'),
        where('userEmail', '==', userEmail)
      );

      const querySnapshot = await getDocs(q);
      const notifications: Notification[] = [];

      querySnapshot.forEach((doc) => {
        notifications.push({
          id: doc.id,
          ...doc.data()
        } as Notification);
      });

      // Sort by timestamp in JavaScript instead of Firestore
      notifications.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      return notifications.slice(0, limit);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return [];
    }
  }

  // Mark notification as read
  async markNotificationAsRead(notificationId: string): Promise<void> {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        isRead: true
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }

  // Send quote response to client
  async sendQuoteResponse(
    clientEmail: string,
    clientName: string,
    quoteId: string,
    response: string,
    adminEmail: string,
    adminName: string
  ): Promise<string> {
    return this.sendMessage({
      fromUserId: 'admin',
      fromUserEmail: adminEmail,
      fromUserName: adminName,
      toUserEmail: clientEmail,
      toUserName: clientName,
      subject: 'Response to Your Quote Request',
      message: response,
      type: 'quote_response',
      relatedQuoteId: quoteId,
      isRead: false,
      isAdminMessage: true
    });
  }

  // Send invoice notification to client
  async sendInvoiceMessage(
    clientEmail: string,
    clientName: string,
    invoiceId: string,
    invoiceNumber: string,
    amount: number,
    adminEmail: string,
    adminName: string
  ): Promise<string> {
    const message = `Your invoice ${invoiceNumber} has been generated for ₹${amount.toLocaleString()}. Please review and make payment as per the terms.`;
    
    return this.sendMessage({
      fromUserId: 'admin',
      fromUserEmail: adminEmail,
      fromUserName: adminName,
      toUserEmail: clientEmail,
      toUserName: clientName,
      subject: `Invoice Generated - ${invoiceNumber}`,
      message,
      type: 'invoice_notification',
      relatedInvoiceId: invoiceId,
      isRead: false,
      isAdminMessage: true
    });
  }

  // Send payment confirmation to admin
  async sendPaymentConfirmation(
    adminEmail: string,
    adminName: string,
    clientEmail: string,
    clientName: string,
    amount: number,
    paymentId: string
  ): Promise<string> {
    const message = `Payment of ₹${amount.toLocaleString()} has been received from ${clientName} (${clientEmail}). Payment ID: ${paymentId}`;
    
    return this.sendMessage({
      fromUserId: 'system',
      fromUserEmail: 'system@avis.com',
      fromUserName: 'System',
      toUserEmail: adminEmail,
      toUserName: adminName,
      subject: 'Payment Received',
      message,
      type: 'payment_notification',
      isRead: false,
      isAdminMessage: false
    });
  }
}

export const communicationService = new CommunicationService();
export default communicationService;
